import type { NuxtApp, NuxtError } from '#app'
import type { AsyncData, AsyncDataOptions, PickFrom, KeysOf } from '#app/composables/asyncData'
import { isIpNotAllowedPostgrestError } from '~/repositories/error'
import { reportError, showIpNotAllowedError } from '~/utils/error'

type Handler<ResT> = (ctx?: NuxtApp) => Promise<ResT>

// showIpNotAllowedErrorの処理も入っていてcaptrue以上のことをしているので
// useAsyncDataWithCaptureの名前は変えたい
export function useAsyncDataWithCapture<
  ResT,
  NuxtErrorDataT = unknown,
  DataT = ResT,
  PickKeys extends KeysOf<DataT> = KeysOf<DataT>,
  DefaultT = null
>(
  handler: Handler<ResT>,
  options?: AsyncDataOptions<ResT, DataT, PickKeys, DefaultT>
): AsyncData<
  PickFrom<DataT, PickKeys> | DefaultT,
  (NuxtErrorDataT extends Error | NuxtError ? NuxtErrorDataT : NuxtError<NuxtErrorDataT>) | null
> {
  const cacheKey = crypto.randomUUID()
  return useAsyncData(cacheKey, captureWrapper(handler), options) as AsyncData<
    PickFrom<DataT, PickKeys> | DefaultT,
    (NuxtErrorDataT extends Error | NuxtError ? NuxtErrorDataT : NuxtError<NuxtErrorDataT>) | null
  >
}

const captureWrapper: <T>(handler: Handler<T>) => Handler<T> = (handler) => {
  return async (ctx) => {
    try {
      return await handler(ctx)
    } catch (e) {
      // ナビゲーションを伴わないuseAsyncDataWithCapture実行時のIP制限エラーをハンドリングする
      // （ナビゲーション時のIP制限エラーはmiddlewareでハンドリング）
      if (isIpNotAllowedPostgrestError(e)) {
        // IP制限によるエラーの場合はエラーページを表示する
        // (useAsyncDataのコールバック内なのでthrowするだけではエラーは伝播しない)
        showIpNotAllowedError()
      } else {
        reportError(e)
      }

      throw e
    }
  }
}
